"use client";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { usePostHog } from "@posthog/next";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Command as CommandPrimitive } from "cmdk";
import Icon from "@/components/library/Icon";
import ShortcutKbd from "@/components/library/ShortcutKbd";
import { findRouteWithAI, AccessibleRoute } from "@/actions/navigation/actions";
import { useRouter } from "@bprogress/next";
import { cn } from "@/lib/utils";
import {
  filterSearchableNavItems,
  type SearchableNavItem,
} from "@/utils/navigation/searchableRoutes";

export type { SearchableNavItem };

type NavSearchProps = {
  items: SearchableNavItem[];
  isMinimized: boolean;
};

// Popular/quick access items to show by default (order determines display priority)
const POPULAR_ROUTES = [
  "/calls",
  "/assistants",
  "/outbound/campaigns",
  "/settings/general",
];

export default function NavSearch({ items, isMinimized }: NavSearchProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [portalMounted, setPortalMounted] = useState(false);
  const [query, setQuery] = useState("");
  const [isPending, startTransition] = useTransition();
  const [aiResult, setAiResult] = useState<string | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef(open);
  const router = useRouter();
  const posthog = usePostHog();

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Calculate position when opening
  useEffect(() => {
    if (open) {
      setPortalMounted(true);
    }
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
      });
      // Focus input after position is set
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open]);

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      setQuery("");
      setAiResult(null);
    }
  }, [open]);

  // Get popular items from the available items
  const popularItems = useMemo(() => {
    return POPULAR_ROUTES.map((href) => items.find((item) => item.href === href))
      .filter(Boolean)
      .slice(0, 4) as SearchableNavItem[];
  }, [items]);

  // Filter items by query
  const filteredItems = useMemo(
    () => filterSearchableNavItems(items, query),
    [items, query]
  );

  // Handle navigation
  const handleSelect = useCallback(
    (href: string) => {
      router.push(href);
      setOpen(false);
    },
    [router]
  );

  const accessibleRoutes = useMemo<AccessibleRoute[]>(() => {
    return items.map((item) => ({
      path: item.href,
      name: item.name,
      description: item.description,
      tags: item.tags,
    }));
  }, [items]);

  // Handle AI search
  const handleAISearch = useCallback(() => {
    const searchQuery = query.trim() || t("navSearch.exampleQuery");
    if (isPending) return;

    setAiResult(null);
    startTransition(async () => {
      const result = await findRouteWithAI(searchQuery, accessibleRoutes);

      if (result.route && result.confidence !== "low") {
        // Track AI-assisted navigation
        posthog.capture("AI Navigation Used", {
          query: searchQuery,
          route: result.route,
          confidence: result.confidence,
        });

        router.push(result.route);
        setOpen(false);
      } else {
        setAiResult(result.reasoningText || t("navSearch.noMatchingPage"));
      }
    });
  }, [query, isPending, router, t, accessibleRoutes]);

  const hasQuery = query.trim().length > 0;
  const showFiltered = hasQuery && filteredItems.length > 0;
  const showNoResults = hasQuery && filteredItems.length === 0;

  // Portal content
  const searchPanel =
    typeof window !== "undefined" &&
    portalMounted &&
    createPortal(
      <AnimatePresence
        onExitComplete={() => {
          if (!openRef.current) {
            setPortalMounted(false);
          }
        }}
      >
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[10000] bg-black/50"
              onClick={() => setOpen(false)}
            />

            {/* Search Panel - positioned at trigger */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="fixed z-[10001]"
              style={{
                top: position.top,
                left: position.left,
                width: Math.max(position.width, 320),
              }}
            >
              <CommandPrimitive
                className={cn(
                  "flex flex-col overflow-hidden rounded-lg",
                  "bg-surface border border-200 shadow-2xl"
                )}
                shouldFilter={false}
              >
                {/* Search Input */}
                <div className="flex items-center border-b border-200 px-3">
                  <Icon
                    icon="magnifying-glass"
                    className="mr-2 h-4 w-4 shrink-0 text-400"
                  />
                  <CommandPrimitive.Input
                    ref={inputRef}
                    value={query}
                    onValueChange={setQuery}
                    placeholder={t("navSearch.placeholder")}
                    className={cn(
                      "flex h-11 w-full bg-transparent py-3 text-sm",
                      "border-none outline-none ring-0 focus:outline-none focus:ring-0 focus:border-none",
                      "placeholder:text-muted-foreground"
                    )}
                  />
                  <ShortcutKbd shortcut="Esc" className="border-200 bg-100 text-400" />
                </div>

                {/* Results List */}
                <CommandPrimitive.List className="max-h-[320px] overflow-y-auto overflow-x-hidden p-1">
                  {/* No results */}
                  {showNoResults && (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      {t("navSearch.noResults", { query })}
                    </div>
                  )}

                  {/* Filtered results */}
                  {showFiltered && (
                    <CommandPrimitive.Group
                      heading={t("navSearch.results")}
                      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                    >
                      {filteredItems.map((item) => (
                        <CommandPrimitive.Item
                          key={item.href}
                          value={item.href}
                          keywords={[item.label, item.name, ...item.tags]}
                          forceMount
                          onSelect={() => handleSelect(item.href)}
                          className={cn(
                            "relative flex cursor-pointer select-none items-center rounded-md px-2 py-2.5 text-sm outline-none",
                            "data-[selected=true]:bg-100"
                          )}
                        >
                          <Icon
                            icon={item.icon as any}
                            className="mr-3 h-4 w-4 text-500"
                          />
                          <span className="font-medium">{item.label}</span>
                        </CommandPrimitive.Item>
                      ))}
                    </CommandPrimitive.Group>
                  )}

                  {/* Quick Access - when no query */}
                  {!hasQuery && popularItems.length > 0 && (
                    <CommandPrimitive.Group
                      heading={t("navSearch.quickAccess")}
                      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                    >
                      {popularItems.map((item) => (
                        <CommandPrimitive.Item
                          key={item.href}
                          value={item.href}
                          keywords={[item.label, item.name, ...item.tags]}
                          forceMount
                          onSelect={() => handleSelect(item.href)}
                          className={cn(
                            "relative flex cursor-pointer select-none items-center rounded-md px-2 py-2.5 text-sm outline-none",
                            "data-[selected=true]:bg-100"
                          )}
                        >
                          <Icon
                            icon={item.icon as any}
                            className="mr-3 h-4 w-4 text-500"
                          />
                          <span className="font-medium">{item.label}</span>
                        </CommandPrimitive.Item>
                      ))}
                    </CommandPrimitive.Group>
                  )}

                  {/* Navigation Assistant */}
                  <div className="border-t border-200 mt-1 pt-1">
                    <CommandPrimitive.Item
                      onSelect={handleAISearch}
                      disabled={isPending}
                      className={cn(
                        "relative flex cursor-pointer select-none items-center rounded-md px-2 py-2.5 text-sm outline-none",
                        "data-[selected=true]:bg-100"
                      )}
                    >
                      {isPending ? (
                        <Icon
                          icon="spinner"
                          className="mr-3 h-4 w-4 text-primary animate-spin"
                        />
                      ) : (
                        <Icon
                          icon="wand-magic-sparkles"
                          className="mr-3 h-4 w-4 text-primary"
                        />
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {hasQuery
                            ? `"${query}"`
                            : t("navSearch.exampleQuery")}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {isPending && t("navSearch.findingPage")}
                          {!isPending && aiResult && aiResult}
                          {!isPending && !aiResult && t("navSearch.navigationAssistant")}
                        </span>
                      </div>
                    </CommandPrimitive.Item>
                  </div>
                </CommandPrimitive.List>
              </CommandPrimitive>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        className={clsx(
          "flex",
          "items-center",
          "gap-2",
          "w-full",
          // 8px sides so the 16px icon sits dead center of the 32px
          // minimized box (the gap/label after it collapse to zero width).
          "px-2",
          "py-2",
          "rounded-lg",
          "bg-100",
          "hover:bg-200",
          "text-500",
          "hover:text-foreground",
          "transition-colors",
          "duration-150",
          "text-sm",
          "border",
          "border-200",
          "hover:border-300"
        )}
      >
        <Icon icon="magnifying-glass" className="w-4 h-4 shrink-0" />
        {/* Collapses in sync with the sidebar width (same --fade-duration)
            instead of unmounting instantly, so the icon never jumps. */}
        <span
          aria-hidden={isMinimized ? true : undefined}
          className="flex flex-1 items-center gap-2 overflow-hidden"
          style={{
            transition: "opacity var(--fade-duration), max-width var(--fade-duration)",
            opacity: isMinimized ? 0 : 1,
            maxWidth: isMinimized ? 0 : "100%",
          }}
        >
          <span className="flex-1 text-left truncate">{t("searchDots")}</span>
          <ShortcutKbd shortcut="⌘K" />
        </span>
      </button>

      {/* Portal content */}
      {searchPanel}
    </>
  );
}
